import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useTheme } from '@emotion/react';
import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Alert, Box, Button, Drawer, Grid, Header, Loader } from '~/components/blocks';
import { useInitialValues } from '~/components/pages/Settings_Organization_WebBookingSettings/WebBookingSettingForm/hooks/use-initial_values';
import {
  GetWebBookingsSettingDocument,
  Practitioner,
  UpdateWebBookingsSettingDocument,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { useScrollToTop } from '~/hooks/use-scroll-to-top';
import { editSettingDrawerState } from '~/state/settings_organization_webbookingsettings/atoms';

import { formatWebBookingsSetting } from '..';
import { WebBookingSettingForm } from '../WebBookingSettingForm';
import { Fields } from '../WebBookingSettingForm/types';

const formatWebBookingsSettingWithId = (loginPractitioner: Practitioner) => {
  const setting = loginPractitioner.organization.webBookingsSetting;
  const baseSetting = formatWebBookingsSetting(loginPractitioner);

  if (!setting || !baseSetting) return;

  return {
    ...setting,
    id: setting.id,
    facetoface: setting.facetoface,
    maxAppointmentCount: setting.maxAppointmentCount,
    telemedicine: setting.telemedicine,
    useHoliday: setting.useHoliday,
  };
};

export const EditWebBookingSettingDrawer = () => {
  const theme = useTheme();
  const [containerRef, scrollToTop] = useScrollToTop<HTMLDivElement>();
  const { verifyRole } = usePractitionerRole();
  const enabledEditPublished = verifyRole('admin') || verifyRole('company_admin');
  const [state, setDrawerState] = useAtom(editSettingDrawerState);
  const resetDrawerState = useResetAtom(editSettingDrawerState);
  const [getWebBookingsSetting, { data, loading: loadingWebBookingsSetting, error: fetchError }] =
    useLazyQuery(GetWebBookingsSettingDocument, {
      fetchPolicy: 'no-cache',
    });
  const [updateWebBookingsSetting, { loading }] = useMutation(UpdateWebBookingsSettingDocument, {
    refetchQueries: [{ query: GetWebBookingsSettingDocument }],
  });
  const setting = useMemo(
    () => (data?.me ? formatWebBookingsSettingWithId(data.me as Practitioner) : null),
    [data?.me],
  );
  const hasOrganizationBusinessHours = useMemo(
    () => (data ? (getMe(data)?.organization.organizationBusinessHours?.length ?? 0) > 0 : false),
    [data],
  );
  const paymentAvailable = useMemo(
    () => (data ? !!getMe(data)?.organization.paymentAvailable : false),
    [data],
  );
  const formRef = useRef<RefAttributeType<typeof WebBookingSettingForm>>(null);
  const initialValues = useInitialValues(data);
  const handleClose = useCallback(() => {
    resetDrawerState();
  }, [resetDrawerState]);
  const handleSend = useCallback(() => {
    if (!formRef.current) return;
    formRef.current.submitForm();
  }, []);
  const handleSubmit = useCallback(
    async (values: Fields) => {
      setDrawerState((state) => {
        return {
          ...state,
          error: undefined,
        };
      });
      try {
        if (!setting) return;

        const businessHours = values.businessHours
          .filter((businessHour) => businessHour.enabled)
          .flatMap((businessHour) =>
            businessHour.timeRanges
              .filter(
                (range) =>
                  range.startHour !== '--' &&
                  range.startMinute !== '--' &&
                  range.endHour !== '--' &&
                  range.endMinute !== '--',
              )
              .map((range) => ({
                ...(range.organizationBusinessHourId
                  ? { organizationBusinessHourId: range.organizationBusinessHourId }
                  : {}),
                dayType: businessHour.dayType,
                startTime: `${range.startHour}:${range.startMinute}`,
                endTime: `${range.endHour}:${range.endMinute}`,
              })),
          );
        if (!values.logo) {
          await updateWebBookingsSetting({
            variables: {
              input: {
                webBookingsSettingId: setting.id,
                published: values.published === 'publish',
                description: values.description || '',
                openingHours: values.openingHours || '',
                website: values.website || '',
                facetoface: values.facetoface === 'enabled',
                maxAppointmentCount: setting.maxAppointmentCount,
                telemedicine: values.telemedicine === 'enabled',
                useHoliday: setting.useHoliday,
                electronicPrescription: setting.electronicPrescription,
                onlineQualificationConfirmation:
                  values.onlineQualificationConfirmation === 'enabled',
                onlineQualificationConfirmationUrl: values.onlineQualificationConfirmationUrl,
                businessHours,
              },
            },
          });
        } else {
          const result = await import('~/utils/upload');
          const uploadResult = await result.upload(values.logo);
          await updateWebBookingsSetting({
            variables: {
              input: {
                webBookingsSettingId: setting.id,
                published: values.published === 'publish',
                description: values.description || '',
                openingHours: values.openingHours || '',
                website: values.website || '',
                logo: uploadResult.signedId,
                facetoface: values.facetoface === 'enabled',
                maxAppointmentCount: setting.maxAppointmentCount,
                telemedicine: values.telemedicine === 'enabled',
                useHoliday: setting.useHoliday,
                electronicPrescription: setting.electronicPrescription,
                onlineQualificationConfirmation:
                  values.onlineQualificationConfirmation === 'enabled',
                onlineQualificationConfirmationUrl: values.onlineQualificationConfirmationUrl,
                businessHours,
              },
            },
          });
        }

        resetDrawerState();
      } catch (error) {
        setDrawerState((state) => {
          return {
            ...state,
            error: error.message,
          };
        });
      }
    },
    [resetDrawerState, setDrawerState, setting, updateWebBookingsSetting],
  );

  useEffect(() => {
    if (state.isOpen) {
      getWebBookingsSetting();
    }
  }, [getWebBookingsSetting, state.isOpen]);

  useEffect(() => {
    if (!fetchError) return;
    setDrawerState((s) => ({ ...s, error: fetchError.message }));
  }, [fetchError, setDrawerState]);

  useEffect(() => {
    if (state.error) {
      scrollToTop();
    }
  }, [scrollToTop, state.error]);

  return (
    <Drawer open={state.isOpen} onClose={handleClose}>
      <Grid height="100%" gridTemplateRows="min-content 1fr min-content" overflow="auto">
        <Header attached="drawer">公開情報</Header>
        <Box overflow="auto" ref={containerRef}>
          {state.error && (
            <Alert status="error" m={`0 ${theme.space.xl} ${theme.space.m}`} whiteSpace="pre-wrap">
              {state.error}
            </Alert>
          )}
          <Box overflow="auto">
            {/* isOpenの判定は、ドロワーの開閉によってUploaderを再レンダリングさせてUploader内のステートをクリアするために入れてる */}
            {state.isOpen && loadingWebBookingsSetting && <Loader open inside />}
            {state.isOpen && !loadingWebBookingsSetting && data && (
              <WebBookingSettingForm
                ref={formRef}
                editPublished={enabledEditPublished}
                initialValues={initialValues}
                disabled={loading}
                onSubmit={handleSubmit}
                electronicPrescription={!!setting?.electronicPrescription}
                hasOrganizationBusinessHours={hasOrganizationBusinessHours}
                paymentAvailable={paymentAvailable}
              />
            )}
          </Box>
        </Box>
        <Drawer.Footer>
          <Button wide="fill" use="base" size="l" loading={loading} onClick={handleSend}>
            保存
          </Button>
        </Drawer.Footer>
      </Grid>
    </Drawer>
  );
};
